import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTenant } from '../lib/tenant-context';

interface BrandConfig {
  id: string;
  name: string;
  slug: string;
  voice_name: string;
  voice_tone: string;
  voice_personality: string;
  terminology: Record<string, string>;
  primary_color: string;
  secondary_color: string;
  logo_url: string;
  trade_in_enabled: number;
  trade_in_excellent_pct: number;
  trade_in_good_pct: number;
  trade_in_fair_pct: number;
  tradeInAcceptedCategories: string[];
  tradeInRejectedItems: string[];
  fabricTechnologies: Record<string, string>;
  sizingGuide: Record<string, string>;
  stores: Store[];
}

interface Store {
  id: string;
  name: string;
  city: string;
  state: string;
  pin_enabled: number;
}

export const Route = createFileRoute('/stores_/$id')({
  component: BrandDetailPage,
});

function BrandDetailPage() {
  const { id } = Route.useParams();
  const { currentTenant } = useTenant();
  const [brand, setBrand] = useState<BrandConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'voice' | 'tradeIn' | 'products'>('voice');

  // Editable fields
  const [voiceName, setVoiceName] = useState('');
  const [voiceTone, setVoiceTone] = useState('friendly');
  const [voicePersonality, setVoicePersonality] = useState('');
  const [tradeInExcellent, setTradeInExcellent] = useState(50);
  const [tradeInGood, setTradeInGood] = useState(35);
  const [tradeInFair, setTradeInFair] = useState(20);
  const [fabricTechnologies, setFabricTechnologies] = useState<Record<string, string>>({});
  const [newFabricName, setNewFabricName] = useState('');
  const [newFabricDesc, setNewFabricDesc] = useState('');

  useEffect(() => {
    loadBrand();
  }, [id, currentTenant.id]);

  const loadBrand = async () => {
    try {
      const response = await fetch(`/api/v1/config/brands/${id}`);
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          setBrand(data);
          setVoiceName(data.voice_name || '');
          setVoiceTone(data.voice_tone || 'friendly');
          setVoicePersonality(data.voice_personality || '');
          setTradeInExcellent(data.trade_in_excellent_pct || 50);
          setTradeInGood(data.trade_in_good_pct || 35);
          setTradeInFair(data.trade_in_fair_pct || 20);
          setFabricTechnologies(data.fabricTechnologies || {});
          setLoading(false);
          return;
        }
      }
      // Use demo data
      const demoData = getTenantDemoBrandConfig(currentTenant.id, id);
      setBrand(demoData);
      setVoiceName(demoData.voice_name);
      setVoiceTone(demoData.voice_tone);
      setVoicePersonality(demoData.voice_personality);
      setTradeInExcellent(demoData.trade_in_excellent_pct);
      setTradeInGood(demoData.trade_in_good_pct);
      setTradeInFair(demoData.trade_in_fair_pct);
      setFabricTechnologies(demoData.fabricTechnologies);
    } catch (error) {
      console.debug('Failed to load brand, using demo data:', error);
      const demoData = getTenantDemoBrandConfig(currentTenant.id, id);
      setBrand(demoData);
      setVoiceName(demoData.voice_name);
      setVoiceTone(demoData.voice_tone);
      setVoicePersonality(demoData.voice_personality);
      setTradeInExcellent(demoData.trade_in_excellent_pct);
      setTradeInGood(demoData.trade_in_good_pct);
      setTradeInFair(demoData.trade_in_fair_pct);
      setFabricTechnologies(demoData.fabricTechnologies);
    } finally {
      setLoading(false);
    }
  };
  
  // Tenant-specific demo brand config
  function getTenantDemoBrandConfig(tenantId: string, brandId: string): BrandConfig {
    if (tenantId === 'arcteryx') {
      return {
        id: brandId,
        name: "Arc'teryx",
        slug: 'arcteryx',
        voice_name: 'Alpine',
        voice_tone: 'professional',
        voice_personality: 'I am Alpine, your Arc\'teryx product expert. I have deep knowledge of alpine environments, technical materials, and the craftsmanship behind every Arc\'teryx piece. I speak with authority about GORE-TEX technologies, Coreloft insulation, and the rigorous testing our gear undergoes. I help customers understand the right gear for their adventures.',
        terminology: { 'jacket': 'shell', 'coat': 'insulated piece' },
        primary_color: '#1a1a1a',
        secondary_color: '#B8860B',
        logo_url: '',
        trade_in_enabled: 1,
        trade_in_excellent_pct: 45,
        trade_in_good_pct: 30,
        trade_in_fair_pct: 15,
        tradeInAcceptedCategories: ['Shell Jackets', 'Insulated Jackets', 'Pants', 'Fleece', 'Accessories'],
        tradeInRejectedItems: ['Underwear', 'Socks', 'Damaged beyond repair'],
        fabricTechnologies: {
          'GORE-TEX Pro': 'The most breathable, durable, and protective waterproof fabric for extended use in the harshest conditions.',
          'GORE-TEX': 'Durably waterproof, windproof and breathable fabric for reliable protection.',
          'Coreloft': 'Synthetic insulation that stays warm when wet and dries quickly.',
          'LEAF': 'Law Enforcement & Armed Forces - our most demanding technical line.',
        },
        sizingGuide: {},
        stores: [
          { id: 'store-arc-van', name: 'Vancouver Flagship', city: 'Vancouver', state: 'BC', pin_enabled: 1 },
          { id: 'store-arc-whi', name: 'Whistler Village', city: 'Whistler', state: 'BC', pin_enabled: 1 },
          { id: 'store-arc-sea', name: 'Seattle Premium', city: 'Seattle', state: 'WA', pin_enabled: 1 },
          { id: 'store-arc-nyc', name: 'NYC SoHo', city: 'New York', state: 'NY', pin_enabled: 0 },
        ],
      };
    } else if (tenantId === 'nespresso') {
      return {
        id: brandId,
        name: 'Nespresso',
        slug: 'nespresso',
        voice_name: 'Nina',
        voice_tone: 'luxury',
        voice_personality: 'I am Nina, your Nespresso sustainability concierge. I combine sophistication with environmental consciousness to guide you through our recycling program. I speak knowledgeably about our commitment to sustainability, the aluminum recycling process, and how every capsule returned makes a difference.',
        terminology: { 'pod': 'capsule', 'coffee maker': 'machine' },
        primary_color: '#6B4423',
        secondary_color: '#000000',
        logo_url: '',
        trade_in_enabled: 1,
        trade_in_excellent_pct: 100,
        trade_in_good_pct: 100,
        trade_in_fair_pct: 100,
        tradeInAcceptedCategories: ['Original Capsules', 'Vertuo Capsules'],
        tradeInRejectedItems: ['Non-Nespresso capsules', 'Machines', 'Accessories'],
        fabricTechnologies: {},
        sizingGuide: {},
        stores: [
          { id: 'store-nes-nyc', name: 'Madison Avenue Boutique', city: 'New York', state: 'NY', pin_enabled: 1 },
          { id: 'store-nes-la', name: 'Beverly Hills', city: 'Los Angeles', state: 'CA', pin_enabled: 1 },
        ],
      };
    }

    // Default: Lululemon
    return {
      id: brandId,
      name: 'lululemon',
      slug: 'lululemon',
      voice_name: 'Luna',
      voice_tone: 'friendly',
      voice_personality: 'I am Luna, your lululemon product expert. I help guests find the perfect gear for their practice, whether that\'s yoga, running, training, or everyday wear. I speak knowledgeably about our fabrics like Nulu, Everlux, and Luxtreme, and I\'m passionate about helping people feel great in what they wear.',
      terminology: { 'customer': 'guest', 'leggings': 'tights' },
      primary_color: '#C41230',
      secondary_color: '#000000',
      logo_url: '',
      trade_in_enabled: 1,
      trade_in_excellent_pct: 50,
      trade_in_good_pct: 35,
      trade_in_fair_pct: 20,
      tradeInAcceptedCategories: ['Tights', 'Tops', 'Outerwear', 'Accessories'],
      tradeInRejectedItems: ['Underwear', 'Socks', 'Swim'],
      fabricTechnologies: {
        'Nulu': 'Buttery soft, lightweight fabric with a naked sensation. Perfect for yoga.',
        'Everlux': 'Cool-to-touch, sweat-wicking fabric that dries fast. Great for training.',
        'Luxtreme': 'Smooth, sweat-wicking with shape retention. Ideal for running.',
        'Silverescent': 'Powered by X-STATIC, inhibits growth of odor-causing bacteria.',
      },
      sizingGuide: {},
      stores: [
        { id: 'store-lulu-nj', name: 'Short Hills Mall', city: 'Short Hills', state: 'NJ', pin_enabled: 1 },
        { id: 'store-lulu-nyc', name: 'Fifth Avenue Flagship', city: 'New York', state: 'NY', pin_enabled: 1 },
        { id: 'store-lulu-la', name: 'Santa Monica', city: 'Santa Monica', state: 'CA', pin_enabled: 0 },
      ],
    };
  }

  const saveChanges = async () => {
    setSaving(true);
    try {
      const response = await fetch(`/api/v1/config/brands/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voiceName,
          voiceTone,
          voicePersonality,
          tradeInExcellentPct: tradeInExcellent,
          tradeInGoodPct: tradeInGood,
          tradeInFairPct: tradeInFair,
          fabricTechnologies,
        }),
      });

      if (response.ok) {
        loadBrand();
      }
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const addFabric = () => {
    if (newFabricName && newFabricDesc) {
      setFabricTechnologies({
        ...fabricTechnologies,
        [newFabricName]: newFabricDesc,
      });
      setNewFabricName('');
      setNewFabricDesc('');
    }
  };

  const removeFabric = (name: string) => {
    const updated = { ...fabricTechnologies };
    delete updated[name];
    setFabricTechnologies(updated);
  };

  if (loading) {
    return (
      <div className="max-w-4xl p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: '#00FBA9' }} />
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="max-w-4xl p-12 text-center">
        <p className="mb-4" style={{ color: '#A9A9A9' }}>Brand not found</p>
        <Link to="/stores" className="text-sm" style={{ color: '#00FBA9' }}>Back to Stores</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Link to="/stores" className="text-sm flex items-center gap-1 mb-4" style={{ color: '#A9A9A9' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to Store Configuration
        </Link>
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 flex items-center justify-center text-white font-bold text-xl"
            style={{ backgroundColor: brand.primary_color }}
          >
            {brand.name[0]}
          </div>
          <div>
            <h1 className="text-2xl font-light" style={{ color: '#171717' }}>{brand.name}</h1>
            <p className="text-sm" style={{ color: '#A9A9A9' }}>{brand.slug} • {brand.stores.length} stores</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
        <button
          onClick={() => setActiveTab('voice')}
          className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
          style={{
            borderColor: activeTab === 'voice' ? '#00FBA9' : 'transparent',
            color: activeTab === 'voice' ? '#171717' : '#A9A9A9',
          }}
        >
          AI Voice Settings
        </button>
        <button
          onClick={() => setActiveTab('tradeIn')}
          className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
          style={{
            borderColor: activeTab === 'tradeIn' ? '#00FBA9' : 'transparent',
            color: activeTab === 'tradeIn' ? '#171717' : '#A9A9A9',
          }}
        >
          Trade-In Rules
        </button>
        <button
          onClick={() => setActiveTab('products')}
          className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
          style={{
            borderColor: activeTab === 'products' ? '#00FBA9' : 'transparent',
            color: activeTab === 'products' ? '#171717' : '#A9A9A9',
          }}
        >
          Product Knowledge
        </button>
      </div>

      {/* Voice Settings Tab */}
      {activeTab === 'voice' && (
        <div className="space-y-6">
          <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <h2 className="font-medium mb-4" style={{ color: '#171717' }}>AI Educator Persona</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Name</label>
                <input
                  type="text"
                  value={voiceName}
                  onChange={(e) => setVoiceName(e.target.value)}
                  className="w-full px-3 py-2 border"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  placeholder="e.g., Lulu, Pat, Alex"
                />
                <p className="text-xs mt-1" style={{ color: '#A9A9A9' }}>The AI will introduce itself with this name</p>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Voice Tone</label>
                <select
                  value={voiceTone}
                  onChange={(e) => setVoiceTone(e.target.value)}
                  className="w-full px-3 py-2 border"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                >
                  <option value="friendly">Friendly - Warm and approachable</option>
                  <option value="professional">Professional - Polished and informative</option>
                  <option value="casual">Casual - Relaxed and conversational</option>
                  <option value="luxury">Luxury - Sophisticated and exclusive</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Personality Description</label>
                <textarea
                  value={voicePersonality}
                  onChange={(e) => setVoicePersonality(e.target.value)}
                  className="w-full px-3 py-2 border min-h-[100px]"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  placeholder="Describe how the AI should behave, what language to use, what to emphasize..."
                />
                <p className="text-xs mt-1" style={{ color: '#A9A9A9' }}>This becomes part of the AI's system prompt</p>
              </div>
            </div>
          </div>

          <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <h2 className="font-medium mb-4" style={{ color: '#171717' }}>Preview</h2>
            <div className="bg-gray-50 p-4">
              <p className="text-sm italic" style={{ color: '#171717' }}>
                "Hi! I'm {voiceName || '[Name]'}, your {voiceTone} {brand.name} AI Educator. 
                {voicePersonality ? ` ${voicePersonality.slice(0, 100)}...` : ' How can I help you today?'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Trade-In Tab */}
      {activeTab === 'tradeIn' && (
        <div className="space-y-6">
          <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <h2 className="font-medium mb-4" style={{ color: '#171717' }}>Credit Percentages</h2>
            <p className="text-sm mb-4" style={{ color: '#A9A9A9' }}>
              Percentage of original retail price offered as trade-in credit by condition
            </p>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Excellent</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={tradeInExcellent}
                    onChange={(e) => setTradeInExcellent(parseInt(e.target.value) || 0)}
                    className="w-20 px-3 py-2 border text-right"
                    style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                    min="0"
                    max="100"
                  />
                  <span style={{ color: '#A9A9A9' }}>%</span>
                </div>
                <p className="text-xs mt-1" style={{ color: '#A9A9A9' }}>Like new, with tags</p>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Good</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={tradeInGood}
                    onChange={(e) => setTradeInGood(parseInt(e.target.value) || 0)}
                    className="w-20 px-3 py-2 border text-right"
                    style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                    min="0"
                    max="100"
                  />
                  <span style={{ color: '#A9A9A9' }}>%</span>
                </div>
                <p className="text-xs mt-1" style={{ color: '#A9A9A9' }}>Gently used, minor wear</p>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Fair</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={tradeInFair}
                    onChange={(e) => setTradeInFair(parseInt(e.target.value) || 0)}
                    className="w-20 px-3 py-2 border text-right"
                    style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                    min="0"
                    max="100"
                  />
                  <span style={{ color: '#A9A9A9' }}>%</span>
                </div>
                <p className="text-xs mt-1" style={{ color: '#A9A9A9' }}>Visible wear, still wearable</p>
              </div>
            </div>
          </div>

          <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <h2 className="font-medium mb-4" style={{ color: '#171717' }}>Example Calculation</h2>
            <div className="bg-gray-50 p-4">
              <p className="text-sm" style={{ color: '#171717' }}>
                For a $100 item:
              </p>
              <ul className="mt-2 space-y-1 text-sm" style={{ color: '#A9A9A9' }}>
                <li>Excellent condition: <strong style={{ color: '#00FBA9' }}>${tradeInExcellent}</strong> credit</li>
                <li>Good condition: <strong style={{ color: '#00FBA9' }}>${tradeInGood}</strong> credit</li>
                <li>Fair condition: <strong style={{ color: '#00FBA9' }}>${tradeInFair}</strong> credit</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Product Knowledge Tab */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <h2 className="font-medium mb-4" style={{ color: '#171717' }}>Fabric Technologies</h2>
            <p className="text-sm mb-4" style={{ color: '#A9A9A9' }}>
              The AI will use these descriptions when discussing materials
            </p>
            <div className="space-y-3">
              {Object.entries(fabricTechnologies).map(([name, desc]) => (
                <div key={name} className="flex items-start gap-3 p-3 bg-gray-50">
                  <div className="flex-1">
                    <p className="font-medium text-sm" style={{ color: '#171717' }}>{name}</p>
                    <p className="text-sm" style={{ color: '#A9A9A9' }}>{desc}</p>
                  </div>
                  <button
                    onClick={() => removeFabric(name)}
                    className="text-xs px-2 py-1 rounded"
                    style={{ color: '#FF4C00' }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: '#171717' }}>Add Fabric</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newFabricName}
                  onChange={(e) => setNewFabricName(e.target.value)}
                  className="w-32 px-3 py-2 border text-sm"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  placeholder="Name"
                />
                <input
                  type="text"
                  value={newFabricDesc}
                  onChange={(e) => setNewFabricDesc(e.target.value)}
                  className="flex-1 px-3 py-2 border text-sm"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  placeholder="Description"
                />
                <button
                  onClick={addFabric}
                  disabled={!newFabricName || !newFabricDesc}
                  className="px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: '#FAFAFA', color: '#171717', border: '1px solid rgba(169,169,169,0.3)' }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={saveChanges}
          disabled={saving}
          className="px-6 py-3 rounded-full text-sm font-medium disabled:opacity-50"
          style={{ backgroundColor: '#00FBA9', color: '#171717' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

